import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { pdv, products, categories, paymentMethods } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ShoppingCart, Plus, Minus, X, CreditCard, Banknote, 
  QrCode, Check, Trash2, Zap, Search, AlertCircle, Wallet,
  User, Truck, Split
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function VendaRapidaPage() {
  const navigate = useNavigate();
  const [productsList, setProductsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Payment modal
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [receivedAmount, setReceivedAmount] = useState('');
  
  // NOVO: Múltiplos pagamentos
  const [splitPayment, setSplitPayment] = useState(false);
  const [payments, setPayments] = useState([]); // [{payment_method_id, amount, received_amount}]
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);
  const [currentPaymentAmount, setCurrentPaymentAmount] = useState('');
  const [currentReceivedAmount, setCurrentReceivedAmount] = useState('');
  
  // Cash validation
  const [cashStatus, setCashStatus] = useState({ isOpen: false, checked: false });
  const [showCashWarning, setShowCashWarning] = useState(false);
  
  // NOVOS ESTADOS - Cliente e Entrega
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [customerName, setCustomerName] = useState('');
  
  // Entrega
  const [deliveryType, setDeliveryType] = useState('PICKUP');
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryFeeSource, setDeliveryFeeSource] = useState(null);
  const [deliveryFeeOverridden, setDeliveryFeeOverridden] = useState(false);

  // MODAL DE OPÇÕES/VARIAÇÕES
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    loadData();
    checkCashStatus();
    loadDrivers();
  }, []);

  const checkCashStatus = async () => {
    try {
      const response = await api.get(`${API_URL}/api/cash/session/current`);
      setCashStatus({ isOpen: response.data.has_open_session, checked: true });
    } catch {
      setCashStatus({ isOpen: false, checked: true });
    }
  };

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes, methodsRes] = await Promise.all([
        products.list(),
        categories.list(),
        paymentMethods.list()
      ]);
      setProductsList(productsRes.data);
      setCategoriesList(categoriesRes.data);
      setPaymentMethodsList(methodsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };
  
  const loadDrivers = async () => {
    try {
      const response = await api.get(`${API_URL}/api/drivers`);
      setDrivers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar entregadores:', error);
    }
  };
  
  // Buscar clientes
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const response = await api.get(`${API_URL}/api/customers?search=${encodeURIComponent(query)}`);
      setCustomerResults(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setCustomerResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, []);
  
  // Debounce para busca de clientes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);
  
  // Calcular taxa de entrega
  const calculateDeliveryFee = useCallback(async () => {
    if (deliveryType !== 'OWN_DELIVERY') {
      setDeliveryFee(0);
      setDeliveryFeeSource(null);
      return;
    }
    
    try {
      const response = await api.post(`${API_URL}/api/menu-admin/delivery/calculate`, {
        order_subtotal: cartTotal
      });
      
      if (!deliveryFeeOverridden) {
        setDeliveryFee(response.data.delivery_fee || 0);
        setDeliveryFeeSource(response.data.fee_source);
      }
    } catch (error) {
      console.error('Erro ao calcular frete:', error);
      if (!deliveryFeeOverridden) {
        setDeliveryFee(0);
        setDeliveryFeeSource('DEFAULT');
      }
    }
  }, [deliveryType, deliveryFeeOverridden]);
  
  useEffect(() => {
    if (cart.length > 0) {
      calculateDeliveryFee();
    }
  }, [deliveryType, cart.length, calculateDeliveryFee]);
  
  const selectCustomer = (customer) => {
    setSelectedCustomerId(customer.id);
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerSearch('');
    setCustomerResults([]);
  };
  
  const clearCustomer = () => {
    setSelectedCustomerId(null);
    setSelectedCustomer(null);
    setCustomerName('');
  };
  
  const handleDeliveryFeeChange = (value) => {
    const fee = parseFloat(value) || 0;
    setDeliveryFee(fee);
    setDeliveryFeeOverridden(true);
    setDeliveryFeeSource('MANUAL');
  };

  // Carregar opções do produto
  const loadProductOptions = async (productId) => {
    try {
      setLoadingOptions(true);
      const response = await api.get(`${API_URL}/api/stock/products/${productId}/option-groups`);
      return response.data || [];
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      return [];
    } finally {
      setLoadingOptions(false);
    }
  };

  // Quando clica no produto
  const handleProductClick = async (product) => {
    const options = await loadProductOptions(product.id);
    
    if (options && options.length > 0) {
      setSelectedProduct(product);
      setProductOptions(options);
      setSelectedOptions({});
      setShowOptionsModal(true);
    } else {
      addToCartDirect(product, []);
    }
  };

  // Adiciona ao carrinho (com ou sem opções)
  const addToCartDirect = (product, modifiers) => {
    let totalPrice = product.price;
    
    modifiers.forEach(mod => {
      totalPrice += (mod.unit_price || mod.price || 0) * (mod.quantity || 1);
    });

    const cartKey = modifiers.length > 0 
      ? `${product.id}-${JSON.stringify(modifiers.map(m => m.option_id || m.name))}`
      : product.id;
    
    const existing = cart.find(item => item.cart_key === cartKey);
    
    if (existing) {
      setCart(cart.map(item =>
        item.cart_key === cartKey
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        cart_key: cartKey,
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: totalPrice,
        modifiers: modifiers,
        notes: ''
      }]);
    }
    
    setShowOptionsModal(false);
    setSelectedProduct(null);
    setProductOptions([]);
    setSelectedOptions({});
  };

  // Toggle opção selecionada
  const toggleOption = (groupId, option, isRequired, maxSelections) => {
    setSelectedOptions(prev => {
      const current = prev[groupId] || [];
      const exists = current.find(o => o.id === option.id);
      
      if (exists) {
        return { ...prev, [groupId]: current.filter(o => o.id !== option.id) };
      } else {
        if (maxSelections === 1) {
          return { ...prev, [groupId]: [option] };
        } else if (current.length < (maxSelections || 99)) {
          return { ...prev, [groupId]: [...current, option] };
        }
        return prev;
      }
    });
  };

  // Confirmar seleção de opções
  const confirmOptions = () => {
    const modifiers = [];
    Object.entries(selectedOptions).forEach(([groupId, options]) => {
      const group = productOptions.find(g => g.id === groupId);
      options.forEach(opt => {
        modifiers.push({
          option_id: opt.id,
          option_name: opt.name,
          option_group_name: group?.name || "Adicionais",
          unit_price: opt.price_modifier || 0,
          quantity: 1
        });
      });
    });
    addToCartDirect(selectedProduct, modifiers);
  };

  const addToCart = (product) => {
    handleProductClick(product);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const finalTotal = cartTotal + (deliveryType === 'OWN_DELIVERY' ? deliveryFee : 0);

  const handlePaymentClick = () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio!');
      return;
    }
    
    if (!cashStatus.isOpen) {
      setShowCashWarning(true);
      return;
    }
    
    setShowPayment(true);
  };

  // Calcular total já pago (múltiplos pagamentos)
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const remainingAmount = finalTotal - totalPaid;

  // Adicionar pagamento à lista
  const addPayment = () => {
    if (!currentPaymentMethod || !currentPaymentAmount || parseFloat(currentPaymentAmount) <= 0) {
      toast.error('Selecione a forma e valor do pagamento');
      return;
    }
    
    const method = paymentMethodsList.find(m => m.id === currentPaymentMethod);
    const amount = parseFloat(currentPaymentAmount);
    
    setPayments([...payments, {
      payment_method_id: currentPaymentMethod,
      method_name: method?.name || 'Pagamento',
      amount: amount,
      received_amount: currentReceivedAmount ? parseFloat(currentReceivedAmount) : null
    }]);
    
    // Reset campos
    setCurrentPaymentMethod(null);
    setCurrentPaymentAmount('');
    setCurrentReceivedAmount('');
  };

  // Remover pagamento da lista
  const removePayment = (index) => {
    setPayments(payments.filter((_, i) => i !== index));
  };

  const handleConfirmSale = async () => {
    // Validar se tem pagamento
    if (splitPayment) {
      if (payments.length === 0) {
        toast.error('Adicione pelo menos um pagamento');
        return;
      }
      if (totalPaid < finalTotal) {
        toast.error(`Falta pagar ${formatCurrency(remainingAmount)}`);
        return;
      }
    } else {
      if (!selectedPayment) {
        toast.error('Selecione a forma de pagamento');
        return;
      }
    }
    
    if (cart.length === 0) return;
    
    setProcessing(true);
    try {
      const payload = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          modifiers: item.modifiers || [],
          notes: item.notes || ''
        })),
        customer_name: customerName || 'Balcão',
        notes: orderNotes || '',
        customer_id: selectedCustomerId || null,
        delivery: deliveryType === 'OWN_DELIVERY' ? {
          delivery_type: deliveryType,
          driver_id: (selectedDriverId && selectedDriverId !== 'none') ? selectedDriverId : null,
          delivery_fee: deliveryFee || 0,
          fee_source: deliveryFeeSource || 'DEFAULT',
          fee_overridden: deliveryFeeOverridden || false
        } : null
      };
      
      // Se pagamento dividido, usar array de payments
      if (splitPayment && payments.length > 0) {
        payload.payments = payments.map(p => ({
          payment_method_id: p.payment_method_id,
          amount: p.amount,
          received_amount: p.received_amount
        }));
      } else {
        // Pagamento único (compatibilidade)
        payload.payment_method_id = selectedPayment;
        payload.received_amount = receivedAmount ? parseFloat(receivedAmount) : null;
      }
      
      const result = await pdv.quickSale(payload);
      
      const change = result.data.change || 0;
      const paymentsInfo = result.data.payments || [];
      
      toast.success(
        <div>
          <p className="font-bold">Venda concluída!</p>
          <p>Total: {formatCurrency(finalTotal)}</p>
          {paymentsInfo.length > 1 && (
            <p className="text-xs text-slate-500">
              {paymentsInfo.map(p => `${p.method}: ${formatCurrency(p.amount)}`).join(' | ')}
            </p>
          )}
          {change > 0 && <p className="text-emerald-600">Troco: {formatCurrency(change)}</p>}
        </div>
      );
      
      // Reset all fields
      setCart([]);
      setOrderNotes('');
      setShowPayment(false);
      setSelectedPayment(null);
      setReceivedAmount('');
      setSplitPayment(false);
      setPayments([]);
      setCurrentPaymentMethod(null);
      setCurrentPaymentAmount('');
      setCurrentReceivedAmount('');
      setSelectedCustomerId(null);
      setSelectedCustomer(null);
      setCustomerName('');
      setDeliveryType('PICKUP');
      setSelectedDriverId(null);
      setDeliveryFee(0);
      setDeliveryFeeOverridden(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar venda');
    } finally {
      setProcessing(false);
    }
  };

  const handleGoToCash = () => {
    setShowCashWarning(false);
    navigate('/cash');
  };

  // Filter products
  const filteredProducts = productsList.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category_id === selectedCategory;
    const matchesSearch = !searchTerm || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get payment method icon
  const getPaymentIcon = (method) => {
    const name = method.name?.toLowerCase() || '';
    if (name.includes('dinheiro')) return Banknote;
    if (name.includes('pix')) return QrCode;
    return CreditCard;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex h-[calc(100vh-120px)]" data-testid="venda-rapida-page">
        {/* Left Side - Products */}
        <div className="flex-1 flex flex-col overflow-hidden pr-4">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Zap className="text-amber-500" />
              Venda Rápida
            </h1>
          </div>

          {/* Search + Categories */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar produto..."
                className="pl-9"
                data-testid="search-product-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                data-testid="category-all"
                className={cn(
                  'px-3 py-2 rounded-lg font-medium transition-all text-sm',
                  selectedCategory === 'all'
                    ? 'bg-[#FF6B6B] text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                )}
              >
                Todos
              </button>
              {categoriesList.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                  className={cn(
                    'px-3 py-2 rounded-lg font-medium transition-all text-sm',
                    selectedCategory === cat.id
                      ? 'bg-[#FF6B6B] text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  )}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  data-testid={`product-${product.id}`}
                  className="bg-white rounded-xl border border-slate-200 p-4 hover:ring-2 ring-[#FF6B6B] transition-all active:scale-95 text-left"
                >
                  <div className="h-16 bg-slate-100 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url.startsWith("http") ? product.image_url : `${API_URL}${product.image_url}`} alt={product.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display="none"; e.target.nextSibling.style.display="block"; }} />
                    ) : null}
                    <span className="text-2xl" style={{display: product.image_url ? "none" : "block"}}>🍔</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-lg font-bold text-[#FF6B6B]" style={{ fontFamily: 'Barlow Condensed' }}>
                    {formatCurrency(product.price)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Cart */}
        <div className="w-96 bg-white rounded-xl border border-slate-200 flex flex-col shadow-sm">
          {/* Cart Header */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <ShoppingCart size={20} />
              Carrinho
              {cartCount > 0 && (
                <span className="bg-[#FF6B6B] text-white text-xs px-2 py-0.5 rounded-full">
                  {cartCount}
                </span>
              )}
            </h2>
            {cart.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearCart} className="text-slate-500 hover:text-red-500">
                <Trash2 size={16} />
              </Button>
            )}
          </div>
          
          {/* NOVA SEÇÃO - Cliente */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 text-slate-700 font-medium mb-2">
              <User size={16} />
              <span className="text-sm">Cliente</span>
            </div>
            
            {selectedCustomer ? (
              <div className="flex items-center justify-between bg-white rounded-lg p-2 border">
                <div>
                  <p className="font-medium text-slate-900 text-sm">{selectedCustomer.name}</p>
                  <p className="text-xs text-slate-500">{selectedCustomer.phone || 'Sem telefone'}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={clearCustomer}>
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Buscar cliente..."
                    className="pl-8 h-9 text-sm"
                    data-testid="customer-search-input"
                  />
                </div>
                
                {customerResults.length > 0 && (
                  <div className="bg-white rounded-lg border max-h-32 overflow-y-auto">
                    {customerResults.map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => selectCustomer(customer)}
                        className="w-full text-left p-2 hover:bg-slate-50 border-b last:border-b-0 text-sm"
                      >
                        <p className="font-medium text-slate-900">{customer.name}</p>
                        <p className="text-xs text-slate-500">{customer.phone}</p>
                      </button>
                    ))}
                  </div>
                )}
                
                {searchingCustomers && (
                  <p className="text-xs text-slate-500 text-center">Buscando...</p>
                )}
                
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Ou digite nome avulso..."
                  className="h-9 text-sm"
                  data-testid="customer-name-input"
                />
              </div>
            )}
          </div>
          
          {/* NOVA SEÇÃO - Tipo de Entrega */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 text-slate-700 font-medium mb-2">
              <Truck size={16} />
              <span className="text-sm">Entrega</span>
            </div>
            
            <div className="flex gap-2 mb-3">
              <Button
                variant={deliveryType === 'PICKUP' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDeliveryType('PICKUP')}
                className={cn(
                  "flex-1 text-xs",
                  deliveryType === 'PICKUP' ? 'bg-[#FF6B6B] hover:bg-[#FF7A5A]' : ''
                )}
                data-testid="delivery-type-pickup"
              >
                Retirada
              </Button>
              <Button
                variant={deliveryType === 'OWN_DELIVERY' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDeliveryType('OWN_DELIVERY')}
                className={cn(
                  "flex-1 text-xs",
                  deliveryType === 'OWN_DELIVERY' ? 'bg-[#FF6B6B] hover:bg-[#FF7A5A]' : ''
                )}
                data-testid="delivery-type-delivery"
              >
                Entrega
              </Button>
            </div>
            
            {deliveryType === 'OWN_DELIVERY' && (
              <div className="space-y-2">
                <Select value={selectedDriverId || 'none'} onValueChange={(val) => setSelectedDriverId(val === 'none' ? null : val)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="driver-select">
                    <SelectValue placeholder="Motoboy (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {drivers.map((driver) => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Taxa:</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={deliveryFee}
                    onChange={(e) => handleDeliveryFeeChange(e.target.value)}
                    className="h-8 text-sm flex-1"
                    data-testid="delivery-fee-input"
                  />
                  {deliveryFeeOverridden && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => {
                        setDeliveryFeeOverridden(false);
                        calculateDeliveryFee();
                      }}
                    >
                      Reset
                    </Button>
                  )}
                </div>
                {deliveryFeeSource && !deliveryFeeOverridden && (
                  <p className="text-xs text-slate-400">Fonte: {deliveryFeeSource}</p>
                )}
              </div>
            )}
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {cart.length > 0 ? cart.map((item, idx) => (
              <div key={item.cart_item_id || idx} className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-900">{item.product_name}</p>
                    {/* Mostrar variações selecionadas */}
                    {item.modifiers && item.modifiers.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {item.modifiers.map((mod, midx) => (
                          <p key={midx} className="text-xs flex items-center gap-1">
                            <span className="text-emerald-500">+</span>
                            <span className="text-slate-700">{mod.option_name}</span>
                            {mod.price > 0 && (
                              <span className="text-emerald-600">(+{formatCurrency(mod.price)})</span>
                            )}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeFromCart(item.cart_item_id || item.product_id)}
                    className="text-slate-400 hover:text-red-500 transition-colors ml-2"
                  >
                    <X size={16} />
                  </button>
                </div>
                {/* Campo OBS por item */}
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="OBS: ex. mal passado, sem cebola"
                    value={item.notes || ''}
                    onChange={(e) => {
                      const key = item.cart_key || item.cart_item_id || item.product_id;
                      setCart(prev => prev.map(ci => 
                        (ci.cart_key || ci.cart_item_id || ci.product_id) === key
                          ? { ...ci, notes: e.target.value }
                          : ci
                      ));
                    }}
                    className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 focus:outline-none"
                    data-testid={`item-notes-${idx}`}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.cart_item_id || item.product_id, -1)}
                    >
                      <Minus size={14} />
                    </Button>
                    <span className="font-bold text-lg w-8 text-center">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.cart_item_id || item.product_id, 1)}
                    >
                      <Plus size={14} />
                    </Button>
                  </div>
                  <span className="font-bold text-[#FF6B6B]">
                    {formatCurrency(item.unit_price * item.quantity)}
                  </span>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-400">
                <ShoppingCart size={48} className="mx-auto mb-3 opacity-30" />
                <p>Carrinho vazio</p>
                <p className="text-sm">Clique nos produtos para adicionar</p>
              </div>
            )}
          </div>

          {/* Cash Warning */}
          {!cashStatus.isOpen && cashStatus.checked && (
            <div className="mx-4 mb-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-800 text-sm">Caixa Fechado</p>
                <p className="text-xs text-amber-600">Abra o caixa para vender</p>
              </div>
            </div>
          )}

          {/* OBS Geral do Pedido */}
          <div className="px-4 py-2 border-t border-slate-200">
            <label className="text-xs font-medium text-slate-500 mb-1 block">OBS do Pedido</label>
            <input
              type="text"
              placeholder="Ex: cliente VIP, sem talheres, embalar separado..."
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 focus:outline-none"
              data-testid="order-notes-input"
            />
          </div>

          {/* Cart Footer */}
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <div className="space-y-1 mb-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(cartTotal)}</span>
              </div>
              {deliveryType === 'OWN_DELIVERY' && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">Taxa entrega:</span>
                  <span className="font-medium">{formatCurrency(deliveryFee)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-slate-900 font-medium">Total:</span>
                <span className="text-2xl font-bold text-[#FF6B6B]" style={{ fontFamily: 'Barlow Condensed' }}>
                  {formatCurrency(finalTotal)}
                </span>
              </div>
            </div>
            <Button
              onClick={handlePaymentClick}
              className="w-full h-12 text-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              disabled={cart.length === 0 || (!cashStatus.isOpen && cashStatus.checked)}
              data-testid="finalize-sale-btn"
            >
              <Check size={24} className="mr-2" />
              Finalizar Venda
            </Button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-center">Forma de Pagamento</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* Total */}
            <div className="text-center mb-4 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Total a Pagar</p>
              <p className="text-4xl font-bold text-[#FF6B6B]" style={{ fontFamily: 'Barlow Condensed' }}>
                {formatCurrency(finalTotal)}
              </p>
              {deliveryType === 'OWN_DELIVERY' && (
                <p className="text-xs text-slate-400 mt-1">
                  (Subtotal: {formatCurrency(cartTotal)} + Entrega: {formatCurrency(deliveryFee)})
                </p>
              )}
            </div>
            
            {/* Toggle Split Payment */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <Button
                variant={!splitPayment ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setSplitPayment(false);
                  setPayments([]);
                }}
                className={cn(!splitPayment && 'bg-[#FF6B6B] hover:bg-[#FF7A5A]')}
              >
                Pagamento Único
              </Button>
              <Button
                variant={splitPayment ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setSplitPayment(true);
                  setSelectedPayment(null);
                }}
                className={cn(splitPayment && 'bg-[#FF6B6B] hover:bg-[#FF7A5A]')}
              >
                <Split size={16} className="mr-1" />
                Dividir
              </Button>
            </div>
            
            {/* Pagamento Único */}
            {!splitPayment && (
              <>
                {/* Payment Methods */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {paymentMethodsList.map((method) => {
                    const Icon = getPaymentIcon(method);
                    return (
                      <button
                        key={method.id}
                        onClick={() => setSelectedPayment(method.id)}
                        data-testid={`payment-${method.id}`}
                        className={cn(
                          "p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2",
                          selectedPayment === method.id
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 hover:border-slate-300 text-slate-700'
                        )}
                      >
                        <Icon size={24} />
                        <span className="font-medium text-sm">{method.name}</span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Received Amount (for cash) */}
                {selectedPayment && paymentMethodsList.find(m => m.id === selectedPayment)?.name?.toLowerCase().includes('dinheiro') && (
                  <div className="mb-4">
                    <label className="text-sm text-slate-600 mb-1 block">Valor Recebido</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={receivedAmount}
                      onChange={(e) => setReceivedAmount(e.target.value)}
                      placeholder={formatCurrency(finalTotal)}
                      className="text-center text-xl font-bold"
                      data-testid="received-amount-input"
                    />
                    {receivedAmount && parseFloat(receivedAmount) >= finalTotal && (
                      <p className="text-emerald-600 text-center mt-2 font-bold">
                        Troco: {formatCurrency(parseFloat(receivedAmount) - finalTotal)}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            
            {/* Pagamento Dividido */}
            {splitPayment && (
              <div className="space-y-4">
                {/* Lista de pagamentos adicionados */}
                {payments.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-slate-500 uppercase">Pagamentos Adicionados</p>
                    {payments.map((payment, index) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded-lg p-2 border">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{payment.method_name}</span>
                          <span className="text-sm text-emerald-600 font-bold">{formatCurrency(payment.amount)}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removePayment(index)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="text-slate-600">Total pago:</span>
                      <span className="font-bold text-emerald-600">{formatCurrency(totalPaid)}</span>
                    </div>
                    {remainingAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Falta:</span>
                        <span className="font-bold text-amber-600">{formatCurrency(remainingAmount)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Adicionar novo pagamento */}
                {remainingAmount > 0 && (
                  <div className="border rounded-lg p-3 space-y-3">
                    <p className="text-xs font-medium text-slate-500 uppercase">Adicionar Pagamento</p>
                    
                    {/* Selecionar método */}
                    <div className="grid grid-cols-4 gap-2">
                      {paymentMethodsList.map((method) => {
                        const Icon = getPaymentIcon(method);
                        return (
                          <button
                            key={method.id}
                            onClick={() => setCurrentPaymentMethod(method.id)}
                            className={cn(
                              "p-2 rounded-lg border transition-all flex flex-col items-center gap-1",
                              currentPaymentMethod === method.id
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                : 'border-slate-200 hover:border-slate-300 text-slate-700'
                            )}
                          >
                            <Icon size={18} />
                            <span className="font-medium text-xs">{method.name}</span>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Valor */}
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Valor</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={currentPaymentAmount}
                          onChange={(e) => setCurrentPaymentAmount(e.target.value)}
                          placeholder={formatCurrency(remainingAmount)}
                          className="text-sm"
                        />
                      </div>
                      
                      {/* Valor recebido (se dinheiro) */}
                      {currentPaymentMethod && paymentMethodsList.find(m => m.id === currentPaymentMethod)?.name?.toLowerCase().includes('dinheiro') && (
                        <div className="flex-1">
                          <label className="text-xs text-slate-500 mb-1 block">Recebido</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={currentReceivedAmount}
                            onChange={(e) => setCurrentReceivedAmount(e.target.value)}
                            placeholder="Opcional"
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Atalhos de valor */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPaymentAmount(remainingAmount.toFixed(2))}
                        className="text-xs"
                      >
                        Restante ({formatCurrency(remainingAmount)})
                      </Button>
                      {[10, 20, 50, 100].map(val => (
                        <Button
                          key={val}
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPaymentAmount(Math.min(val, remainingAmount).toFixed(2))}
                          className="text-xs"
                          disabled={val > remainingAmount}
                        >
                          R$ {val}
                        </Button>
                      ))}
                    </div>
                    
                    <Button
                      onClick={addPayment}
                      disabled={!currentPaymentMethod || !currentPaymentAmount}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus size={16} className="mr-1" />
                      Adicionar Pagamento
                    </Button>
                  </div>
                )}
                
                {remainingAmount <= 0 && payments.length > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-emerald-700 font-medium">Pagamento completo!</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSale}
              disabled={
                processing || 
                (splitPayment ? (payments.length === 0 || remainingAmount > 0) : !selectedPayment)
              }
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-sale-btn"
            >
              {processing ? 'Processando...' : 'Confirmar Venda'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Warning Dialog */}
      <Dialog open={showCashWarning} onOpenChange={setShowCashWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-6 h-6" />
              Caixa Fechado
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Para realizar vendas, é necessário abrir o caixa primeiro.
          </p>
          <div className="py-6 flex justify-center">
            <div className="p-6 bg-amber-50 rounded-full">
              <Wallet className="w-16 h-16 text-amber-500" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCashWarning(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGoToCash} className="bg-[#FF6B6B] hover:bg-[#FF7A5A]">
              Abrir Caixa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL DE OPÇÕES/VARIAÇÕES */}
      <Dialog open={showOptionsModal} onOpenChange={setShowOptionsModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedProduct?.name}
              <span className="text-sm font-normal text-slate-500">
                {formatCurrency(selectedProduct?.price || 0)}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {loadingOptions ? (
            <div className="py-8 text-center text-slate-500">Carregando opções...</div>
          ) : (
            <div className="space-y-4">
              {productOptions.map(group => (
                <div key={group.id} className="border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">{group.name}</h4>
                    <span className="text-xs text-slate-500">
                      {group.is_required ? "Obrigatório" : "Opcional"}
                      {group.max_selections > 1 && ` • Máx: ${group.max_selections}`}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {group.options?.map(option => {
                      const isSelected = (selectedOptions[group.id] || []).find(o => o.id === option.id);
                      return (
                        <button
                          key={option.id}
                          onClick={() => toggleOption(group.id, option, group.is_required, group.max_selections)}
                          className={cn(
                            "w-full flex justify-between items-center p-2 rounded border transition-all",
                            isSelected 
                              ? "border-[#FF6B6B] bg-red-50 text-[#FF6B6B]" 
                              : "border-slate-200 hover:border-slate-300"
                          )}
                        >
                          <span>{option.name}</span>
                          <span className="font-medium">
                            {option.price_modifier > 0 ? `+${formatCurrency(option.price_modifier)}` : "Grátis"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowOptionsModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={confirmOptions}
              className="bg-[#FF6B6B] hover:bg-[#ff5252]"
            >
              Adicionar ao Carrinho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}

export default VendaRapidaPage;
